import { z } from 'zod';
import { signalVariantSchema } from './signal.js';
// --- Enums ---
export const cellClassificationSchema = z.enum([
    'INPUT',
    'FORMULA',
    'LABEL',
    'EMPTY',
    'OUTPUT',
]);
export const dataTypeSchema = z.enum([
    'percentage',
    'currency',
    'currency_per_sf',
    'currency_per_unit_year',
    'integer',
    'decimal',
    'months',
    'years',
    'date',
    'string',
    'boolean',
    'location',
]);
export const prioritySchema = z.enum(['critical', 'high', 'medium', 'low']);
// --- Component schemas ---
export const validRangeSchema = z.object({
    min: z.number().optional(),
    max: z.number().optional(),
    softMin: z.number().optional(),
    softMax: z.number().optional(),
});
export const dataValidationSchema = z.object({
    type: z.string(),
    operator: z.string().optional(),
    formula1: z.string().optional(),
    formula2: z.string().optional(),
});
// --- Input / Output definitions ---
export const inputDefinitionSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    description: z.string().default(''),
    sheet: z.string(),
    cell: z.string(),
    dataType: dataTypeSchema,
    displayFormat: z.string().default(''),
    storageFormat: z.string().default(''),
    required: z.boolean().default(false),
    // Foundational input — set by the ingestion heuristic at schema-extract
    // time (`agents-tailoring-launch` Phase E Session 3, 2026-05-05). Distinct
    // from `required` (UI form-validation flag): `foundational: true` means
    // "agent should ask for this BEFORE running calculate; absence makes
    // downstream outputs meaningless." Consumed only by structural-rules.ts
    // Detector 3 (and possibly Phase F's band-check priority logic). UI does
    // NOT read this field. Default `false` so existing schemas without the
    // field parse as `foundational: false` (back-compat preserved).
    foundational: z.boolean().default(false),
    defaultValue: z.any().optional(),
    defaultSource: z.string().optional(),
    validRange: validRangeSchema.optional(),
    /**
     * Closed-set string validation. When non-empty, `set_inputs` writes must
     * match one of these literals exactly — mismatches emit a structured
     * recoverable error with the valid set, letting the agent self-correct.
     *
     * Hand-authored per-model via `packages/agents/src/models/<model-id>/
     * input-enums.json` sidecar (factory-time merge — re-ingest to refresh).
     * Mirrors the `validRange { min, max }` convention for numeric inputs.
     *
     * Surfaced by `new-development-input-roundtrip` Session 2 (2026-05-26):
     * agent over-interpreted "Mixed-Use Multifamily" user prose and wrote
     * `ProductType: "Mixed-Use"` — a non-existent product type. Generalized
     * fix is signal-driven (any string input the schema author marks with
     * this field gets validated); no hardcoded name lists in the validator.
     */
    validValues: z.array(z.string()).min(1).optional(),
    affects: z.array(z.string()).default([]),
    affectsOutputs: z.array(z.string()).default([]),
    typicalRange: z.string().optional(),
    benchmarkSource: z.string().optional(),
    defaultConfidence: z.number().min(0).max(1).optional(),
    category: z.string(),
    priority: prioritySchema,
});
/**
 * Known location-typed input named-ranges across reference workbooks.
 *
 * Legacy fallback used by `isLocationInput()` when the schema's `dataType`
 * is the pre-cmv-P4 string-typed `"string"` instead of the canonical
 * `"location"` enum value. mfv1 uses `Location`; multifamily-operating-asset
 * uses `PropertyAddress`. New workbooks should ship `dataType: 'location'`
 * directly so they get picked up via the dataType branch without needing
 * to be added here. (Added 2026-05-20 by Inc-B of variables-counter-coherence
 * fix-partner: prevents location inputs from being seeded as defaults
 * regardless of whether the workbook author shipped a stray `defaultValue`.)
 */
export const LEGACY_LOCATION_INPUT_NAMES = new Set([
    'Location',
    'PropertyAddress',
]);
/**
 * Predicate: is this schema input the property-location input?
 *
 * Location inputs MUST NEVER be seeded as defaults at conversation-create
 * time — the user must explicitly resolve a property location (at minimum
 * city-level granularity per the cmv-P4 + 2026-05-20 contract). A defaulted
 * location creates the illusion that the model is "complete" when the agent
 * actually has no real property data, breaking market-data lookups and the
 * completeness gate.
 *
 * Returns true if either (a) the schema declares `dataType: 'location'`
 * (canonical, post-cmv-P4), OR (b) the input name matches a known
 * legacy location named-range (back-compat for workbooks still on the
 * pre-cmv-P4 `dataType: 'string'`).
 *
 * Consumed by: `apps/api/src/modules/conversations/conversations.service.v2.ts:create()`
 * (Inc-2 seed loop) AND `packages/agents/src/lib/provenance/tracker.ts:initializeDefaults()`
 * — both surfaces must skip location inputs in lockstep.
 */
export function isLocationInput(input) {
    if (input.dataType === 'location')
        return true;
    if (LEGACY_LOCATION_INPUT_NAMES.has(input.name))
        return true;
    return false;
}
export const outputDefinitionSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    description: z.string().default(''),
    sheet: z.string(),
    cell: z.string(),
    dataType: dataTypeSchema,
    displayFormat: z.string().default(''),
    dependsOn: z.array(z.string()).default([]),
    dependsOnInputs: z.array(z.string()).default([]),
    category: z.string(),
    sensitivityEligible: z.boolean().default(false),
});
export const intermediateDefinitionSchema = z.object({
    name: z.string(),
    sheet: z.string(),
    cell: z.string(),
    formula: z.string().optional(),
});
// --- Dependency graph ---
export const dependencyEdgeSchema = z.object({
    from: z.string(),
    to: z.string(),
    type: z.enum(['direct', 'transitive']),
    formula: z.string().optional(),
});
// --- Validation ---
export const validationRuleSchema = z.object({
    id: z.string(),
    field: z.string(),
    condition: z.string(),
    severity: z.enum(['error', 'warning', 'info']),
    action: z.enum(['block', 'warn', 'suggest', 'auto_fix']),
    message: z.string(),
    suggestedFix: z.string().optional(),
    parameters: z.record(z.string(), z.any()).optional(),
});
// --- Sensitivity ---
export const sensitivityTestDefSchema = z.object({
    id: z.string(),
    name: z.string(),
    inputNamedRange: z.string(),
    outputNamedRanges: z.array(z.string()),
    method: z.enum(['absolute', 'relative']),
    defaultStepSize: z.number(),
    steps: z.number().default(2),
});
// --- Named ranges ---
export const namedRangeInfoSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    ref: z.string(),
    sheet: z.string(),
    cell: z.string(),
    cellType: cellClassificationSchema,
    value: z.any().optional(),
    formula: z.string().optional(),
    displayFormat: z.string().optional(),
    dataValidation: dataValidationSchema.optional(),
    dependsOn: z.array(z.string()).default([]),
    affects: z.array(z.string()).default([]),
});
// --- Sheet info ---
export const sheetInfoSchema = z.object({
    name: z.string(),
    rows: z.number(),
    cols: z.number(),
    formulaCount: z.number().default(0),
    inputCount: z.number().default(0),
});
// --- HyperFormula seed ---
export const cellDataSchema = z.object({
    value: z.any().optional(),
    formula: z.string().optional(),
});
export const hyperFormulaSeedSheetSchema = z.object({
    name: z.string(),
    cells: z.record(z.string(), cellDataSchema),
});
export const hyperFormulaSeedSchema = z.object({
    sheets: z.array(hyperFormulaSeedSheetSchema),
    namedRanges: z.record(z.string(), z.object({
        sheet: z.string(),
        cell: z.string(),
    })),
});
// --- Supplementary data ---
export const uxBehaviorNoteSchema = z.object({
    fieldName: z.string(),
    content: z.string(),
});
export const outputRegistryEntrySchema = z.object({
    name: z.string(),
    displayName: z.string(),
});
// --- Disambiguation rules (Pillar 2 — agents-tailoring-launch Phase D) ---
//
// Hand-curated per-model rules covering residual lookalike pairs, legacy-field
// traps, and table-vs-scalar conflicts that Phase C's structural-rules
// generator can't catch mechanically. Authored by Pope via PR on a sidecar
// file at `packages/agents/src/models/<id>/disambig-rules.json`; merged into
// the schema at agent-factory time and rendered into the cached prefix as a
// `<disambiguation>` block.
//
// `superRefine` enforces two cross-field invariants the rest of the system
// relies on:
//   1. `choose === 'ASK'` requires `question` — without it, every "ASK"
//      trigger generates ad-hoc phrasing, defeating the consistency goal.
//   2. `choose ∈ options[]` (or `'ASK'`) — prevents typos / stale-rename bugs
//      where the canonical pick references a field that no longer exists in
//      `options[]`. The factory-time merge does NOT verify `choose ∈
//      schema.inputs[].name` here — that lives in the agents-side merge tests
//      where the schema is in scope.
export const disambiguationRuleSchema = z
    .object({
    // Natural-language description; LLM interprets at first-hop. NOT a regex.
    // Min length 8 to discourage one-word triggers like "rent" — too ambiguous.
    trigger: z.string().min(8),
    // At least two candidates to disambiguate.
    options: z.array(z.string().min(1)).min(2),
    // Canonical input name (must appear in `options[]`), OR literal "ASK".
    choose: z.string().min(1),
    // Required when `choose === "ASK"`. The canonical phrasing the agent uses
    // ("Is that the in-place rent or the pro-forma rent?").
    question: z.string().optional(),
    // PR-review surface — one-line rationale, ≥8 chars to discourage drive-by edits.
    why: z.string().min(8),
})
    .superRefine((rule, ctx) => {
    if (rule.choose === 'ASK' && !rule.question) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['question'],
            message: 'question is required when choose === "ASK"',
        });
    }
    if (rule.choose !== 'ASK' && !rule.options.includes(rule.choose)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['choose'],
            message: 'choose must be in options[] or the literal "ASK"',
        });
    }
});
// --- Table Input Definitions (for structured/tabular data like unit mix) ---
export const tableColumnDefSchema = z.object({
    key: z.string(),
    displayName: z.string(),
    dataType: dataTypeSchema,
    cell: z.string(),
});
export const tableRowDefSchema = z.object({
    index: z.number(),
    label: z.string(),
    cells: z.record(z.string(), z.string()),
    defaultValues: z.record(z.string(), z.any()),
    active: z.boolean(),
});
export const aggregateInputDefSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    targetColumn: z.string(),
    formulaCell: z.string(),
    aggregation: z.enum(['sum', 'weighted_average']),
    weightColumn: z.string().optional(),
});
export const tableInputDefinitionSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    sheet: z.string(),
    description: z.string().default(''),
    topLeftCell: z.string(),
    bottomRightCell: z.string(),
    rowHeaderColumn: z.string().optional(),
    colHeaderRow: z.number().optional(),
    columns: z.array(tableColumnDefSchema),
    rows: z.array(tableRowDefSchema),
    maxRows: z.number(),
    aggregates: z.array(aggregateInputDefSchema).default([]),
});
// --- ModelSchema (top-level) ---
export const modelSchemaSchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.string(),
    createdAt: z.string(),
    sourceFile: z.string(),
    sheets: z.array(sheetInfoSchema),
    inputs: z.array(inputDefinitionSchema),
    outputs: z.array(outputDefinitionSchema),
    intermediates: z.array(intermediateDefinitionSchema).default([]),
    dependencyGraph: z.array(dependencyEdgeSchema),
    validationRules: z.array(validationRuleSchema).default([]),
    sensitivityTests: z.array(sensitivityTestDefSchema).default([]),
    outputRegistry: z.array(outputRegistryEntrySchema).default([]),
    uxBehaviorNotes: z.array(uxBehaviorNoteSchema).default([]),
    // agents-tailoring-launch Phase D — additive optional field. Sidecar-merged
    // at agent-factory time (`packages/agents/src/lib/schema/disambiguation-loader.ts`).
    disambiguationRules: z.array(disambiguationRuleSchema).optional(),
    allNamedRanges: z.array(namedRangeInfoSchema),
    tableInputs: z.array(tableInputDefinitionSchema).default([]),
    displayConfig: z.lazy(() => modelDisplayConfigSchema).optional(),
    // agent-ux-optimization Phase 6 Session 1 (2026-05-08) — drives which Signal
    // tab variant component renders. NULL/undefined = Signal disabled for this
    // model; admin assigns via /admin?tab=models. Mirrors the
    // model_registry.signal_variant column added in the same session.
    // `.optional().nullable()` (vs `.default(null)`) keeps the inferred type
    // optional so the many existing `ModelSchema` literals across the agents
    // package stay valid without re-spreading. Consumers normalize via
    // `schema.signal_variant ?? null` (the API merge layer in
    // `apps/api/src/modules/models/models.service.ts:getSchema` does this).
    signal_variant: signalVariantSchema.nullable().optional(),
});
// --- Output thresholds (for status coloring and feedback) ---
export const outputThresholdsSchema = z.object({
    warn: z
        .object({
        below: z.number().optional(),
        above: z.number().optional(),
    })
        .optional(),
    error: z
        .object({
        below: z.number().optional(),
        above: z.number().optional(),
    })
        .optional(),
    feedbackText: z.string().optional(),
    feedbackFix: z.string().optional(),
});
// --- Band violations (Pillar 7 — agents-tailoring-launch Phase F) ---
//
// Emitted by `checkBandViolations(engine, schema)` (in @arcanum-hq/agents)
// when an output's current engine value falls outside an authored
// `outputOverrides[*].thresholds` band. Threaded through the per-turn
// [MODEL CONTEXT] block via `buildModelContextBlock`'s `latestBandViolations`
// opt; consumed by RULE 11 in the conversational system prompt.
//
// Severity precedence: `error` wins over `warn` when both bands match.
// Sort order is severity-first, then `sortOrder` ascending — see the
// helper's `severityRank` + sort contract documented at
// `packages/agents/src/lib/agent/band-check.ts`.
//
// `currentValue` is required: the helper never emits a violation without
// the actual numeric value (defensive against partial engine states).
export const bandViolationSeveritySchema = z.enum(['warn', 'error']);
export const bandViolationSchema = z.object({
    outputName: z.string(),
    currentValue: z.number(),
    severity: bandViolationSeveritySchema,
    band: z.object({
        below: z.number().optional(),
        above: z.number().optional(),
    }),
    feedbackText: z.string(),
    feedbackFix: z.string(),
    sortOrder: z.number(),
});
// --- Per-output display override ---
export const outputDisplayOverrideSchema = z.object({
    panel: z.enum(['performance', 'capital_stack', 'none']).default('none'),
    displayFormatOverride: z.string().optional(),
    thresholds: outputThresholdsSchema.optional(),
    sortOrder: z.number().default(0),
});
// --- Per-input collection override ---
export const inputCollectionOverrideSchema = z.object({
    requiredOverride: z.boolean().optional(),
    hidden: z.boolean().default(false),
});
// --- Sensitivity override (admin configuration per test) ---
export const sensitivityOverrideSchema = z.object({
    enabled: z.boolean().optional(),
    displayNameOverride: z.string().optional(),
    defaultStepSize: z.number().optional(),
    steps: z.number().optional(),
    method: z.enum(['absolute', 'relative']).optional(),
});
// --- Signal-page field mappings (deal-signal-overhaul Phase 1, 2026-05-11) ---
//
// Per-model declarative mapping from Signal-page rendered element → named-range.
// Populated by ingestion-time merge of
// `packages/agents/src/models/<id>/signal-field-mappings.json` sidecar files
// (Phase 1 Session 3). Consumed by web Signal renderers
// (`<HeroMetricCell>`, `<SnapshotBox>`, `<CapitalStackBar>`, `<AssumptionCard>`,
// `<ValueAddStory>`'s template-substitution metric resolver, `<ProjectUsesTable>`,
// `<BeforeAfterComparison>`) via `displayConfig.signalFieldMappings.<key>`.
//
// Resolver pattern (Phase 4): "outputs first, inputs fallback, `—` only if both
// miss". The mapping IS the schema-driven replacement for the legacy
// `findFirstNumber([...candidates])` arrays in the existing Signal renderers
// (`apps/web/src/components/builder/signal/Signal{OperatingAsset,NewDevelopment}.tsx`).
// See spec.md Decisions Locked for why the candidate-key array pattern is
// retired (it was the bug that produced the `—` placeholders).
//
// All fields optional — each model declares only the keys it supports.
// Variant-specific keys (e.g. `heroBasisToReplacementCost` for operating-asset,
// `heroStabilizedCOC` for new-development) coexist on the same shape; renderers
// consume only the keys their variant cares about. Unknown keys are stripped
// by Zod default behavior.
export const signalFieldMappingsSchema = z.object({
    // Hero metrics (5 cells per variant — single hero strip per Lovable reference)
    heroIRR: z.string().optional(),
    heroEquityMultiple: z.string().optional(),
    heroBasisToReplacementCost: z.string().optional(), // operating-asset
    heroGoingInCapRate: z.string().optional(), // operating-asset
    heroStabilizedDSCR: z.string().optional(), // both variants
    heroStabilizedCOC: z.string().optional(), // new-development
    heroStabilizedNOIMargin: z.string().optional(), // new-development
    // Snapshot box (5 fields per variant)
    snapshotPropertyName: z.string().optional(),
    snapshotAddress: z.string().optional(),
    snapshotUnits: z.string().optional(),
    snapshotVintageClass: z.string().optional(), // operating-asset
    snapshotProductType: z.string().optional(), // new-development
    snapshotLocation: z.string().optional(), // new-development
    snapshotTotalCapitalization: z.string().optional(), // operating-asset
    snapshotTotalDevelopmentCost: z.string().optional(), // new-development
    snapshotProjectStart: z.string().optional(), // new-development
    snapshotYear1NOI: z.string().optional(), // operating-asset
    snapshotAcquisitionDate: z.string().optional(), // operating-asset
    // (added 2026-05-11 from Session 1 matrix walk —
    // missing from initial Zod shape in design.md)
    // Capital stack slices (operating-asset: senior + equity; new-development: construction-loan + equity)
    capitalStackSeniorLoan: z.string().optional(),
    capitalStackEquity: z.string().optional(),
    capitalStackSubordinate: z.string().optional(),
    capitalStackLoanRate: z.string().optional(),
    capitalStackTotalCapitalization: z.string().optional(),
    // Incentive sources (new-development — DealSummary Capital Stack rows G15/G16;
    // default 0, always rendered so the Sources table mirrors the DealSummary).
    capitalStackIncentive1: z.string().optional(),
    capitalStackIncentive2: z.string().optional(),
    // Before/After comparison (operating-asset only — 5 rows × 2 columns = 10 keys)
    comparisonAvgRent_inPlace: z.string().optional(),
    comparisonAvgRent_stabilized: z.string().optional(),
    comparisonOccupancy_inPlace: z.string().optional(),
    comparisonOccupancy_stabilized: z.string().optional(),
    comparisonNOI_inPlace: z.string().optional(),
    comparisonNOI_stabilized: z.string().optional(),
    comparisonOperatingMargin_inPlace: z.string().optional(),
    comparisonOperatingMargin_stabilized: z.string().optional(),
    comparisonImpliedValue_inPlace: z.string().optional(),
    comparisonImpliedValue_stabilized: z.string().optional(),
    // Project uses (new-development only — 5 rows; %TDC + $/Unit derived from these + total)
    usesLandCost: z.string().optional(),
    usesHardCosts: z.string().optional(),
    usesSoftCosts: z.string().optional(),
    // Financing soft cost — summed INTO the Soft Costs row so it equals the
    // DealSummary G10 (TotalSoftCost + FinancingSoftCost). Distinct from
    // `usesFinancingCosts`, which is the Interest Reserve (DealSummary G11).
    usesFinancingSoftCost: z.string().optional(),
    usesFinancingCosts: z.string().optional(),
    usesTotalDevelopmentCost: z.string().optional(),
    // Unit mix (segment-count keys; renderer derives percentages)
    unitMixStudio: z.string().optional(),
    unitMix1Bed: z.string().optional(),
    unitMix1BedDen: z.string().optional(),
    unitMix2Bed: z.string().optional(),
    unitMix3Bed: z.string().optional(),
    unitMixTotalUnits: z.string().optional(),
});
// --- Value-add narrative (deal-signal-overhaul Phase 4 Session 1, 2026-05-11) ---
//
// 3-phase storyboard rendered by `<ValueAddStory>` on the Signal page. Each
// phase carries a status (past/current/future drives the visual treatment),
// a short narrative line, and optional bullets. Narrative + bullets support
// `{key}` template tokens that resolve via `signalFieldMappings` keys at
// render time (see apps/web/src/lib/value-add-narrative.ts:renderValueAddNarrative).
//
// Per-model sidecar JSON at `packages/agents/src/models/<model-id>/value-add-narrative.json`;
// merged into `displayConfig.valueAddNarrative` at ingestion via
// `loadValueAddNarrativeSidecar` (sibling to Phase-1's signal-field-mappings loader).
//
// `phases.length === 3` is the canonical shape (mirrors the 3-column layout).
// Status enum matches `<PhaseCard>`'s existing `PhaseStatus` literal union.
export const valueAddPhaseSchema = z.object({
    title: z.string(),
    status: z.enum(['past', 'current', 'future']),
    narrative: z.string(),
    bullets: z.array(z.string()).optional(),
});
export const valueAddNarrativeSchema = z.object({
    phases: z.array(valueAddPhaseSchema).length(3),
});
// --- Full model display configuration ---
export const modelDisplayConfigSchema = z.object({
    // Panel assignments (which outputs show where)
    performanceOutputs: z.array(z.string()).default([]),
    capitalStackOutputs: z.array(z.string()).default([]),
    // Input collection order for guided mode
    inputCollectionOrder: z.array(z.string()).default([]),
    // Per-output overrides (format, thresholds, panel, sort)
    outputOverrides: z
        .record(z.string(), outputDisplayOverrideSchema)
        .default({}),
    // Per-input overrides (required, hidden)
    inputOverrides: z
        .record(z.string(), inputCollectionOverrideSchema)
        .default({}),
    // Key metric names returned after set_inputs (replaces hardcoded KEY_METRIC_NAMES)
    keyMetricNames: z.array(z.string()).default([]),
    // Model description
    description: z.string().default(''),
    // Per-test sensitivity overrides (keyed by test ID)
    sensitivityOverrides: z
        .record(z.string(), sensitivityOverrideSchema)
        .default({}),
    // Admin-created custom sensitivity tests
    customSensitivityTests: z.array(sensitivityTestDefSchema).default([]),
    // Per-model Signal-page field mappings (deal-signal-overhaul Phase 1, 2026-05-11)
    // Populated at ingestion via sidecar JSON merge. Optional — back-compat with
    // pre-feature models that haven't been re-ingested.
    signalFieldMappings: signalFieldMappingsSchema.optional(),
    // Per-model value-add storyboard narrative (deal-signal-overhaul Phase 4 Session 1).
    // 3-phase template with `{key}` tokens resolving via signalFieldMappings keys.
    // Populated at ingestion via sidecar JSON merge. Optional — pre-Phase-4 models
    // render an admin-fix-required empty state on the Signal page's value-add row.
    valueAddNarrative: valueAddNarrativeSchema.optional(),
});
// --- Model summary (API response) ---
export const modelSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.string(),
    inputCount: z.number(),
    /**
     * Total addressable inputs = scalar `schema.inputs.length` + sum of
     * `schema.tableInputs[i].rows.length × columns.length` (each table cell is
     * one addressable input from the user's perspective).
     *
     * The canonical denominator for ALL builder-side variable counters
     * (TRACKED, MODEL OVERVIEW, PROGRESS, chip "X set", admin ModelList).
     * `inputCount` (scalar-only) is retained for back-compat but should NOT
     * be used as a counter denominator — it under-counts whenever the model
     * has tables, leading to the pre-fix "50/43" overflow on MODEL OVERVIEW.
     *
     * Optional + nullable for back-compat: rows ingested before the Inc-1
     * field landed have `addressable_input_count IS NULL` and consumers
     * fall back to `inputCount`. Re-ingest via /admin UI populates it.
     * Added by variables-counter-coherence Inc-1 (2026-05-20).
     */
    addressableInputCount: z.number().int().nonnegative().optional(),
    outputCount: z.number(),
    isActive: z.boolean(),
    description: z.string().optional(),
    latestVersion: z.string().optional(),
    /**
     * GCS URL to `layout.json` artifact. NULL on pre-layout rows (the Model tab
     * renders `<LayoutUnavailablePanel>` for those). Added by
     * in-platform-model-view Phase 1 (2026-04-25). `.default(null)` ensures
     * pre-Phase-1 fixtures parse cleanly.
     */
    layoutUrl: z.string().nullable().default(null),
    // agent-ux-optimization Phase 6 Session 2 (2026-05-08) — Signal tab variant
    // surfaced on the list response so the admin `<ModelList>` can render the
    // current value in the Signal Variant column. Same `.optional()` shape as
    // the other two surfaces (modelSchemaSchema, modelRegistryRowSchema) for
    // consistent back-compat. Consumers normalize via `?? null`.
    signal_variant: signalVariantSchema.nullable().optional(),
});
// --- Model version summary (version list API) ---
export const modelVersionSummarySchema = z.object({
    id: z.string(),
    version: z.string(),
    name: z.string(),
    inputCount: z.number(),
    /** See `modelSummarySchema.addressableInputCount`. */
    addressableInputCount: z.number().int().nonnegative().optional(),
    outputCount: z.number(),
    isActive: z.boolean(),
    isLatest: z.boolean(),
    createdAt: z.string(),
    /** Per-version `layout.json` URL — see `modelSummarySchema.layoutUrl`. */
    layoutUrl: z.string().nullable().default(null),
});
// --- Schema diff (model versioning) ---
export const schemaDiffSchema = z.object({
    added: z.array(z.object({ name: z.string(), type: z.string() })),
    removed: z.array(z.object({ name: z.string(), type: z.string() })),
    modified: z.array(z.object({
        name: z.string(),
        field: z.string(),
        oldValue: z.any(),
        newValue: z.any(),
    })),
});
// --- Model registry row (database) ---
export const modelRegistryRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.string(),
    sourceFile: z.string(),
    schemaUrl: z.string(),
    seedUrl: z.string(),
    templateUrl: z.string(),
    inputCount: z.number(),
    /** See `modelSummarySchema.addressableInputCount`. */
    addressableInputCount: z.number().int().nonnegative().optional(),
    outputCount: z.number(),
    isActive: z.boolean(),
    isLatest: z.boolean(),
    createdBy: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * Optional GCS URL to `layout.json` artifact. NULL = pre-layout row (Model
     * tab unavailable for conversations pinned to this version). Added by the
     * in-platform-model-view Phase 1 migration (`20260425000000_add_model_layout_url.sql`).
     */
    layoutUrl: z.string().nullable().default(null),
    // agent-ux-optimization Phase 6 Session 1 (2026-05-08) — see modelSchemaSchema.signal_variant.
    signal_variant: signalVariantSchema.nullable().optional(),
});
